import {
  CheckCircle,
  Info,
  Warning,
  XCircle,
  X,
  Copy,
  Check,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { toast as sonnerToast } from "sonner";

type ToastType = "success" | "info" | "warning" | "error";

interface ToastProps {
  id: string | number;
  type?: ToastType;
  title?: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  /**
   * When set, an icon-only Copy button is shown in the top-right that copies
   * this text to the clipboard (without dismissing the toast). Useful for long
   * messages the user needs to grab verbatim (e.g. GL posting warnings).
   * Only rendered on `warning`/`error` toasts — ignored on success/info.
   */
  copyText?: string;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      aria-label="Copy message"
      title="Copy"
      className="shrink-0 rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
      onClick={() => {
        // Guarded: navigator.clipboard is undefined in insecure contexts and
        // writeText can reject; swallow either way.
        void globalThis.navigator?.clipboard
          ?.writeText(text)
          .then(() => setCopied(true))
          .catch(() => {});
      }}
    >
      {copied ? (
        <Check className="size-4 text-emerald-600" weight="bold" />
      ) : (
        <Copy className="size-4" weight="bold" />
      )}
    </button>
  );
}

const icons: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle className="size-5 text-emerald-600" weight="fill" />,
  info: <Info className="size-5 text-blue-600" weight="fill" />,
  warning: <Warning className="size-5 text-amber-600" weight="fill" />,
  error: <XCircle className="size-5 text-red-600" weight="fill" />,
};

function Toast({
  id,
  type = "success",
  title,
  description,
  action,
  copyText,
}: ToastProps) {
  return (
    <div className="flex w-[364px] items-start gap-3 rounded-lg bg-white p-4 shadow-lg ring-1 ring-black/5">
      <div className="shrink-0">{icons[type]}</div>
      <div className="flex-1 min-w-0">
        {title && <p className="text-sm font-medium text-gray-900">{title}</p>}
        {description && (
          <p className={`text-sm text-gray-500 ${title ? "mt-0.5" : ""}`}>
            {description}
          </p>
        )}
      </div>
      {/* Top-right controls: Copy + text action + Dismiss. Copy is only offered
          on warning/error toasts (the messages worth grabbing verbatim). */}
      <div className="flex shrink-0 items-center gap-1">
        {copyText != null &&
          copyText !== "" &&
          (type === "warning" || type === "error") && (
            <CopyButton text={copyText} />
          )}
        {action && (
          <button
            className="rounded bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-200"
            onClick={() => {
              action.onClick();
              sonnerToast.dismiss(id);
            }}
          >
            {action.label}
          </button>
        )}
        <button
          type="button"
          aria-label="Dismiss notification"
          className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          onClick={() => sonnerToast.dismiss(id)}
        >
          <X className="size-4" weight="bold" />
        </button>
      </div>
    </div>
  );
}

interface ShowToastOptions {
  title?: string;
  description?: string;
  type?: ToastType;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  /**
   * Show an icon-only Copy button (top-right) that copies this text. Only
   * rendered on `warning`/`error` toasts.
   */
  copyText?: string;
}

function toast(options: ShowToastOptions) {
  const { duration, ...rest } = options;
  return sonnerToast.custom((id) => <Toast id={id} {...rest} />, { duration });
}

toast.success = (
  title: string,
  opts?: Omit<ShowToastOptions, "title" | "type">,
) => toast({ title, type: "success", ...opts });

toast.error = (
  title: string,
  opts?: Omit<ShowToastOptions, "title" | "type">,
) => toast({ title, type: "error", ...opts });

toast.warning = (
  title: string,
  opts?: Omit<ShowToastOptions, "title" | "type">,
) => toast({ title, type: "warning", ...opts });

toast.info = (title: string, opts?: Omit<ShowToastOptions, "title" | "type">) =>
  toast({ title, type: "info", ...opts });

toast.dismiss = sonnerToast.dismiss;

export { toast, Toast, type ShowToastOptions, type ToastType };
